import { DatePicker } from 'components/DatePicker';
import { add, addDays, sub } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DaysInMonth } from 'utils';

interface CheckoutDatePickerProps {
  rangeStartDate: Date;
  rangeEndDate: Date;
  selectDate: (date: Date) => void;
  selectedDate: Date | undefined;
  browseCalendar: boolean;
}

interface StepControls {
  stepPrev: () => void;
  stepNext: () => void;
  setDaysToDisplay: React.Dispatch<React.SetStateAction<Date[]>>;
  setCurrentMonth: React.Dispatch<React.SetStateAction<Date>>;
  daysToDisplay: Date[];
  currentMonth: Date;
}

const useStepController = (rangeStartDate: Date, selectedDate?: Date): StepControls => {
  const dateToMatchMonthToShowWith = selectedDate ? selectedDate : rangeStartDate;

  const [currentMonth, setCurrentMonth] = useState<Date>(addDays(dateToMatchMonthToShowWith, 1));

  const initValue = useMemo(() => DaysInMonth(currentMonth), [currentMonth]);
  const [daysToDisplay, setDaysToDisplay] = useState<Date[]>(initValue);

  useEffect(() => {
    setCurrentMonth(dateToMatchMonthToShowWith);
  }, [dateToMatchMonthToShowWith]);

  useEffect(() => {
    setDaysToDisplay(initValue);
  }, [initValue]);

  const stepPrev = useCallback((): void => {
    const prevMonth = sub(currentMonth, { months: 1 });
    setCurrentMonth(prevMonth);
    setDaysToDisplay(DaysInMonth(prevMonth));
  }, [currentMonth]);

  const stepNext = useCallback((): void => {
    const nextMonth = add(currentMonth, { months: 1 });
    setCurrentMonth(nextMonth);
    setDaysToDisplay(DaysInMonth(nextMonth));
  }, [currentMonth]);

  return {
    stepPrev,
    stepNext,
    daysToDisplay,
    currentMonth,
    setDaysToDisplay,
    setCurrentMonth
  };
};

export const CheckoutDatePicker = ({
  rangeStartDate,
  rangeEndDate,
  selectDate,
  selectedDate,
  browseCalendar
}: CheckoutDatePickerProps): JSX.Element => {
  const { stepPrev, stepNext, currentMonth, daysToDisplay } = useStepController(
    rangeStartDate,
    selectedDate
  );

  const todaysDate = new Date();

  return (
    <DatePicker
      rangeStartDate={rangeStartDate}
      rangeEndDate={rangeEndDate}
      selectDate={selectDate}
      selectedDate={selectedDate}
      browseCalendar={browseCalendar}
      stepNext={stepNext}
      stepPrev={stepPrev}
      currentMonth={currentMonth}
      daysToDisplay={daysToDisplay}
      todaysDate={todaysDate}
      shouldDisableWeekends
    />
  );
};
